import React from 'react'
import {
  FileSizeIndicator,
  PlaybackRateButton,
  QuotedVoiceRecording,
  displayDuration,
  useAudioPlayer,
  useMessageContext,
  useStateStore,
  useTranslationContext,
  WaveProgressBar,
} from 'stream-chat-react'
import MessagingFileIcon from './messaging-file-icon'
import MessagingPlayButton from './messaging-play-button'
import { sanitizeAttachmentUrl } from './messaging-utils'

const rootClassName = 'str-chat__message-attachment__voice-recording-widget'

const audioPlayerStateSelector = state => ({
  canPlayRecord: state.canPlayRecord,
  isPlaying: state.isPlaying,
  playbackRate: state.currentPlaybackRate,
  progress: state.progressPercent,
  secondsElapsed: state.secondsElapsed,
})

const VoiceRecordingPlayerUI = ({ audioPlayer }) => {
  const { canPlayRecord, isPlaying, playbackRate, progress, secondsElapsed } =
    useStateStore(audioPlayer?.state, audioPlayerStateSelector) ?? {}
  const displayedDuration = secondsElapsed || audioPlayer.durationSeconds

  return (
    <div className={rootClassName} data-testid="voice-recording-widget">
      <MessagingPlayButton
        isPlaying={!!isPlaying}
        onClick={audioPlayer.togglePlay}
      />
      <div className="str-chat__message-attachment__voice-recording-widget__metadata">
        <div
          className="str-chat__message-attachment__voice-recording-widget__title"
          data-testid="voice-recording-title"
          title={audioPlayer.title}
        >
          {audioPlayer.title}
        </div>
        <div className="str-chat__message-attachment__voice-recording-widget__audio-state">
          <div className="str-chat__message-attachment__voice-recording-widget__timer">
            {audioPlayer.durationSeconds ? (
              displayDuration(displayedDuration)
            ) : (
              <FileSizeIndicator
                fileSize={audioPlayer.fileSize}
                maximumFractionDigits={0}
              />
            )}
          </div>
          <WaveProgressBar
            progress={progress}
            seek={audioPlayer.seek}
            waveformData={audioPlayer.waveformData || []}
          />
        </div>
      </div>
      <div className="str-chat__message-attachment__voice-recording-widget__right-section">
        {isPlaying ? (
          <PlaybackRateButton
            disabled={!canPlayRecord}
            onClick={audioPlayer.increasePlaybackRate}
          >
            {playbackRate?.toFixed(1)}x
          </PlaybackRateButton>
        ) : (
          <MessagingFileIcon
            className="str-chat__file-icon"
            mimeType={audioPlayer.mimeType}
          />
        )}
      </div>
    </div>
  )
}

const VoiceRecordingPlayer = ({ attachment, playbackRates }) => {
  const { t } = useTranslationContext()
  const {
    asset_url: assetUrl,
    duration = 0,
    file_size: fileSize,
    mime_type: mimeType,
    title = t('Voice message'),
    waveform_data: waveformData,
  } = attachment
  const { message, threadList } = useMessageContext() ?? {}
  const safeAssetUrl = sanitizeAttachmentUrl(assetUrl)

  const audioPlayer = useAudioPlayer({
    durationSeconds: duration ?? 0,
    fileSize,
    mimeType,
    playbackRates,
    requester:
      message?.id &&
      `${threadList ? message.parent_id ?? message.id : ''}${message.id}`,
    src: safeAssetUrl,
    title,
    waveformData,
  })

  return audioPlayer ? (
    <VoiceRecordingPlayerUI audioPlayer={audioPlayer} />
  ) : null
}

const MessagingVoiceRecording = ({ attachment, isQuoted }) =>
  isQuoted ? (
    <QuotedVoiceRecording attachment={attachment} />
  ) : (
    <VoiceRecordingPlayer attachment={attachment} />
  )

export default MessagingVoiceRecording
